import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { RefreshCw, Search } from "lucide-react";
import { apiClient } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [search, setSearch] = useState("");

  const fetchOrders = (q = search) =>
    apiClient.get("/admin/orders", { params: q.trim() ? { search: q.trim() } : {} }).then((res) => setOrders(res.data.items));

  useEffect(() => {
    const t = setTimeout(() => fetchOrders(search), 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const syncTracking = async () => {
    setSyncing(true);
    try {
      const { data } = await apiClient.post("/admin/orders/sync-tracking");
      toast.success(data.synced > 0 ? `Synced tracking for ${data.synced} order${data.synced > 1 ? "s" : ""}` : "No new shipments found in ShipStation");
      if (data.synced > 0) fetchOrders();
    } catch {
      toast.error("Could not sync with ShipStation");
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div data-testid="admin-orders-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-display font-semibold text-slate-900">Orders</h1>
        <Button size="sm" variant="outline" className="rounded-md" onClick={syncTracking} disabled={syncing} data-testid="admin-sync-tracking-btn">
          <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${syncing ? "animate-spin" : ""}`} /> {syncing ? "Syncing..." : "Sync Tracking from ShipStation"}
        </Button>
      </div>
      <div className="relative mb-4 max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          className="pl-9"
          placeholder="Search by order #, customer name, or total amount..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="admin-orders-search-input"
        />
      </div>
      <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-x-auto">
        <table className="w-full text-sm text-slate-600">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-4 py-3">Order #</th>
              <th className="px-4 py-3">Customer</th>
              <th className="px-4 py-3">Date</th>
              <th className="px-4 py-3">Total</th>
              <th className="px-4 py-3">Payment</th>
              <th className="px-4 py-3">Fulfillment</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {orders.map((o) => (
              <tr key={o.id} className="border-t border-slate-100" data-testid={`admin-order-row-${o.order_number}`}>
                <td className="px-4 py-3 font-medium text-slate-900">{o.order_number}</td>
                <td className="px-4 py-3">{o.shipping_address.full_name}</td>
                <td className="px-4 py-3">{new Date(o.created_at).toLocaleDateString()}</td>
                <td className="px-4 py-3">${o.total.toFixed(2)}</td>
                <td className="px-4 py-3 capitalize">{o.payment_status}</td>
                <td className="px-4 py-3 capitalize">{o.fulfillment_status}</td>
                <td className="px-4 py-3 text-right"><Link to={`/admin/orders/${o.id}`} className="text-brandblue-600 text-xs font-semibold" data-testid={`admin-view-order-${o.order_number}`}>View</Link></td>
              </tr>
            ))}
          </tbody>
        </table>
        {orders.length === 0 && (
          <p className="p-6 text-center text-slate-400 text-sm" data-testid="admin-no-orders">
            {search.trim() ? "No orders match your search." : "No orders yet."}
          </p>
        )}
      </div>
    </div>
  );
}
